import BaseModel from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand } from 'volta/utils/api';

import { attr, hasMany } from '@ember-data/model';
import { service } from '@ember/service';
import { Type } from '@warp-drive/core-types/symbols';

import type ModelRegistry from 'ember-data/types/registries/model';
import type { IntlService } from 'ember-intl';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type { IResourceName } from 'volta/models/base-model';
import type SessionUserService from 'volta/services/session-user';
import type UserProfile from './user-profile';
import type UserRole from './user-role';
import type {
  ICollectionArgs,
  ICollectionFilter,
  ICollectionProperty,
  ICollectionSort,
  ICollectionSortOption,
  ICreateCollectionViewCmd,
  TCollectionType
} from 'volta/models/types/collection-view';
import type {
  IShareCollectionViewCmd,
  IUpdateCollectionViewCmd
} from 'volta/models/types/collection-view';

export enum CollectionViewEvents {
  CollectionViewCreated = 'CollectionViewCreated',
  CollectionViewUpdated = 'CollectionViewUpdated',
  CollectionViewDeleted = 'CollectionViewDeleted',
  CollectionViewShared = 'CollectionViewShared'
}

export const CollectionViewResourceName: IResourceName = {
  singular: 'collectionView',
  plural: 'collectionViews'
};

export const Commands = {
  UpdateCollectionView: 'UpdateCollectionView',
  DeleteCollectionView: 'DeleteCollectionView',
  CreateCollectionView: 'CreateCollectionView',
  ShareCollectionView: 'ShareCollectionView'
};

export const columnsToProps = (
  columns: IBvTableColumn[],
  filterFn: (c: IBvTableColumn) => boolean = (c) => !c.isCustomField && !c.disabled
): ICollectionProperty[] => {
  return columns.reduce<ICollectionProperty[]>((acc, d) => {
    if (filterFn(d)) {
      acc.push({
        title: d.columnName,
        property: d.valuePath!,
        isFixed: d.isFixed,
        isCustomField: d.isCustomField ?? false,
        isGroupable: d.isGroupable,
        isStatic: d.isStatic ?? false,
        color: d.color
      });
    }

    return acc;
  }, []);
};

export const collectionFiltersToFilterValues = (filters: ICollectionFilter[]): TFilterValues => {
  return filters.reduce<TFilterValues>((acc, f) => {
    acc[f.property] = {
      comparator: f.comparator,
      values: f.values,
      enabled: true
    };
    return acc;
  }, {});
};

export const columnsToSortOptions = (columns: IBvTableColumn[]): ICollectionSortOption[] => {
  return columns.reduce<ICollectionSortOption[]>((acc, d) => {
    if (d.isSortable ?? true) {
      acc.push({
        label: (d.name ?? d.columnName)!,
        value: (d.sortKey ?? d.valuePath)!,
        origin: d.valuePath!,
        color: d.color
      });
    }
    return acc;
  }, []);
};

export const viewIsLocked = (view: CollectionView, sessionUserId: string) => {
  return (
    (view.showForRoles.length > 0 || view.showForUsers.length > 0) &&
    !view.isTemplate &&
    view.createdBy !== sessionUserId
  );
};

export const DEFAULT_COLLECTION_VIEW_ID = '00000000-0000-0000-0000-000000000000';

export default class CollectionView<
  TArgs extends ICollectionArgs = ICollectionArgs
> extends BaseModel {
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;

  static createCollectionView = collectionCommand<
    ICreateCollectionViewCmd,
    CollectionView,
    typeof CollectionView
  >(Commands.CreateCollectionView);

  updateCollectionView = resourceCommand<IUpdateCollectionViewCmd, CollectionView>(
    Commands.UpdateCollectionView,
    { query: { include: 'showForRoles,showForUsers' } }
  );

  shareCollectionView = resourceCommand<IShareCollectionViewCmd, CollectionView>(
    Commands.ShareCollectionView,
    { query: { include: 'showForRoles,showForUsers' } }
  );

  deleteCollectionView = resourceCommand<any, CollectionView>(Commands.DeleteCollectionView);

  @attr('string')
  name!: string;

  @attr('string')
  description!: string;

  @attr('string')
  collectionType!: TCollectionType;

  @attr('number')
  pageSize?: number;

  @attr('string')
  entityType!: keyof ModelRegistry | string;

  @attr('string')
  createdBy!: string;

  @attr('string')
  copiedFrom?: string;

  @attr('boolean', { defaultValue: false })
  isTemplate?: boolean;

  @attr('array')
  properties?: ICollectionProperty[];

  @attr('array', { defaultValue: () => [] })
  filters?: ICollectionFilter[];

  @attr('array', { defaultValue: () => [] })
  groupBy?: string[];

  @attr('array', { defaultValue: () => [] })
  sortBy?: ICollectionSort[];

  @attr({
    defaultValue: () => ({})
  })
  args?: TArgs;

  @hasMany('user-profile', { async: false, inverse: null })
  showForUsers!: UserProfile[];

  @hasMany('user-role', { async: false, inverse: null })
  showForRoles!: UserRole[];

  get idOrDefault() {
    return this.id ?? DEFAULT_COLLECTION_VIEW_ID;
  }

  get isLocked(): boolean {
    return viewIsLocked(this, this.sessionUser.userId);
  }

  toCreateCmd(): ICreateCollectionViewCmd {
    return {
      name: this.name,
      entityType: this.entityType,
      description: this.description,
      collectionType: this.collectionType,
      properties: this.properties?.slice() ?? [],
      pageSize: this.pageSize,
      filters: this.filters?.slice(),
      groupBy: this.groupBy?.slice(),
      sortBy: this.sortBy?.slice(),
      args: this.args ? { ...this.args } : {},
      createdBy: this.sessionUser.userId,
      copiedFrom: this.copiedFrom
    };
  }

  toDuplicateCmd(): ICreateCollectionViewCmd {
    const cmd = this.toCreateCmd();
    return { ...cmd, name: `${cmd.name} (${this.intl.t('copySingular')})` };
  }

  toUpdateCmd(): IUpdateCollectionViewCmd {
    return {
      name: this.name,
      description: this.description,
      collectionType: this.collectionType,
      properties: this.properties?.slice(),
      pageSize: this.pageSize,
      filters: this.filters?.slice(),
      groupBy: this.groupBy?.slice(),
      sortBy: this.sortBy?.slice(),
      args: this.args ? { ...this.args } : {}
    };
  }

  toShareCmd(): IShareCollectionViewCmd {
    return {
      showForRoles: this.showForRoles.map((m) => m.id!),
      showForUsers: this.showForUsers.map((m) => m.id!),
      isTemplate: Boolean(this.isTemplate)
    };
  }

  [Type] = 'collection-view' as const;
}
